import { Button, Flex, Modal, Space, Table, Typography } from "antd";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useParams } from "react-router-dom";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent";
import PermissionCheck from "~Common/PermissionCheck";
import AddPersonModal from "~components/AddPersonModal";
import { ExportModal } from "~components/ExportModal/ExportModal";
import { ImportModal } from "~components/ImportButton/ImportModal";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import SelectNationality from "~components/SelectNationality";
import SelectPersonGender from "~components/SelectPersonGender";
import SelectPersonStatus from "~components/SelectPersonStatus";
import { TableColumnSettings } from "~components/TableColumnSettings/TableColumnSettings";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useTableColumns } from "~hooks/useTableColumns";
import { defaultPersonsExportFieldNames, personsExportFields } from "../constants/exportFields";
import { useExportPersons, useImportPersons } from "../hooks";
import { useDeletePerson, useFetchPersons, useGetPersonsTableColumns } from "./hooks";
import type { SearchFilterParams } from "./types/searchFilterParams";

const PermissionName = PermissionNames.PERSON;

const PersonListPage = () => {
  const { formatMessage } = useIntl();
  const { sport } = useParams();
  const location = useLocation();
  const [modal, modalContextHolder] = Modal.useModal();
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState<boolean>(false);
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions, resetPagination } = useHandlePagination(20);
  const { uiConfiguration } = useSportConfiguration();
  const [filters, setFilters] = useState<Omit<SearchFilterParams, "limit" | "offset">>({
    gender: "",
    nationality: "",
    nameFullLocalContains: location?.state?.search || "",
    status: "",
  });

  const handleFilterChange = (filterKey: keyof SearchFilterParams, value: string) => {
    resetPagination();
    setFilters({ ...filters, [filterKey]: value });
  };

  const listParams = useMemo<SearchFilterParams>(
    () => ({
      limit: pageSize.toString(),
      offset: offset.toString(),
      ...Object.fromEntries(Object.entries(filters).filter(([_, filterValue]) => !!filterValue)),
    }),
    [pageSize, offset, filters],
  );

  const { data, isLoading } = useFetchPersons(listParams);
  const { mutateAsync: deletePerson } = useDeletePerson();
  const { mutateAsync: startImporting } = useImportPersons();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportPersons();

  const onDeleteClick = (personId: string, personName: string) =>
    modal.confirm({
      title: formatMessage({ id: "person.delete" }),
      content: formatMessage({ id: "person.delete.confirm" }, { item: personName }),
      okText: formatMessage({ id: "delete" }),
      onOk: async () => deletePerson(personId),
      centered: true,
    });

  const handlePersonAdded = (localFullName: string) => {
    resetPagination();
    setFilters({
      gender: "",
      nationality: "",
      nameFullLocalContains: localFullName,
      status: "",
    });
  };

  const columns = useGetPersonsTableColumns(onDeleteClick);
  const { columnsToShow, setSelectedColumns } = useTableColumns({
    columnSettingsConfig: {
      columnSettingsLocalStorageKey: `${sport}.tables.settings.columns.persons`,
      tableConfiguration: uiConfiguration.pages.persons.tableColumnGroups,
    },
    columnSortConfig: {
      sorterLocalStorageKey: `${sport}.tables.settings.sort.persons`,
    },
    tableColumns: columns,
  });

  return (
    <>
      {modalContextHolder}
      <AddPersonModal open={isAddPersonModalOpen} handleClose={() => setIsAddPersonModalOpen(false)} onAdd={handlePersonAdded} />
      <Flex vertical gap={8}>
        <Flex align="center" justify="space-between">
          <Typography.Title level={4}>
            <FormattedMessage id="persons" />
          </Typography.Title>
          <Space>
            <SearchFilter
              searchValue={filters.nameFullLocalContains}
              doSearch={(value) => handleFilterChange("nameFullLocalContains", value)}
            />
            <PermissionCheck
              permissionKeys={[PermissionName]}
              permissionOptions={[PermissionOperations.CREATE, PermissionOperations.UPDATE]}
            >
              <ImportModal
                title={formatMessage({ id: "import.modal.title.persons" })}
                description={formatMessage({ id: "import.modal.text.persons" })}
                startImporting={startImporting}
              />
            </PermissionCheck>
            <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.READ]}>
              <ExportModal
                handleExport={startExport}
                defaultFields={defaultPersonsExportFieldNames}
                groupedFields={personsExportFields}
                localStorageKey="export.persons"
                loading={isExportPending}
              />
            </PermissionCheck>
            <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
              <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setIsAddPersonModalOpen(true)}>
                <FormattedMessage id="add.person" />
              </Button>
            </PermissionCheck>
            <TableColumnSettings
              setSelectedColumns={setSelectedColumns}
              tableConfiguration={uiConfiguration.pages.persons.tableColumnGroups}
              localStorageKey={`${sport}.tables.settings.columns.persons`}
              columns={columns.map((x) => ({ key: x.key, title: x.title }))}
            />
          </Space>
        </Flex>
        <Flex align="center" gap={8}>
          <SelectNationality onChange={(value) => handleFilterChange("nationality", value)} disabled={isLoading} />
          <SelectPersonGender onChange={(value) => handleFilterChange("gender", value)} disabled={isLoading} />
          <SelectPersonStatus onChange={(value) => handleFilterChange("status", value)} disabled={isLoading} />
        </Flex>
        <Table
          loading={isLoading}
          size="small"
          columns={columnsToShow}
          rowKey="personId"
          dataSource={data?.persons || []}
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions,
            showQuickJumper: false,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            total: data?.nextLink ? offset + data.persons.length + 1 : offset + (data?.persons?.length || 0),
            showTotal: (total, range) => <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />,
          }}
        />
      </Flex>
    </>
  );
};

export default PersonListPage;
